import React from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'utils/redux'
import { DealActions, DealDetail, DealServicingStartRollEvent, EntityType } from 'api'
import { WindowActions } from 'epics/windowEpic'
import { Locale, Translation } from 'locale'
import { isOpenFin } from 'utils'
import { v4 as uuidv4 } from 'uuid'
import { ActionsButtonProps } from 'view/components/datagrid'
import { ActionItem, SplitButtonDropdown, SplitButtonType } from 'view/components/menus'
import { NotificationType, useModal } from 'view/shared/modal'
import {
  ServicingAdminCloseOpenDialog,
  ServicingAdminRerateDialog,
  ServicingAdminSubstitutionDialog,
  ServicingAdminTerminationDialog,
  ServicingBulkCloseOpenDialog,
  ServicingCloseOpenDialog,
  ServicingRerateDialog,
  ServicingReturnRecallDialog,
  ServicingRollInPlaceDialog,
  ServicingSubstitutionDialog,
  ServicingTerminationDialog,
  ServicingTraderAmendDialog,
  ServicingTraderBulkCancelDialog
} from 'view/shared/servicingDialogs'
import Ticket from './DealTicket'
import {
  allocationUnlocked,
  assignmentUnlocked,
  canAdminCancel,
  canCancel,
  canClearingFailureManuallyCancel,
  canClearingFailureManuallyComplete,
  canClearingFailureRetry,
  canExtendAllocationTime,
  canSaveAllocations,
  canSaveAssignments,
  canServicingAccept,
  canServicingAdminStartSubstitute,
  canServicingSend,
  canServicingStartAdminCloseOpen,
  canServicingStartAdminRerate,
  canServicingStartAdminTerminate,
  canServicingStartCloseOpen,
  canServicingStartRerate,
  canServicingStartReturnRecall,
  canServicingStartRoll,
  canServicingStartRollInPlace,
  canServicingStartSubstitute,
  canServicingStartTerminate,
  canServicingTraderCancel,
  canServicingTraderStartAmend,
  canViewRelatedTrades,
  collateralMarketNotSuspendedOrClosed,
  getDealTitle
} from './utils'

import { platformDetailSelector } from 'selectors/platformSelectors'
import { ShowDialogArgs } from 'store/notification'
import { useShowAllocationDialog } from 'view/shared/allocationDialogs/collateralBasketAllocationDialog'
import { useFundShowAssignmentDialog } from 'view/shared/allocationDialogs/fundAssignmentDialog'
import { useCommonDealActionItems } from './useCommonDealActionItems'
import { RelatedDealsThunks } from 'thunks'
import { useAuthorisedUser } from 'view/hooks'

export interface DealActionsButtonProps extends ActionsButtonProps<DealDetail> {
  disabled?: boolean
}

const createConfirmationHandler = (
  title: Translation,
  message: Translation,
  handler: (dealId: string) => unknown,
  showDialog: (options: ShowDialogArgs) => Promise<Translation>,
  items: DealDetail[]
) => async () => {
  const dialogArgs = {
    type: NotificationType.Confirm,
    title,
    message,
    buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
    cancelButton: Translation.TC_BUTTON_NO,
    defaultButton: Translation.TC_BUTTON_YES
  }
  const dialogResult = await showDialog(dialogArgs)

  if (dialogResult === Translation.TC_BUTTON_YES) {
    items.forEach(dealDetail => {
      handler(dealDetail.deal.dealId)
    })
  }
}

export const DealActionsButton: React.FC<DealActionsButtonProps> = ({ disabled, items, isGroupRow }) => {
  const { showModal, showDialog } = useModal()
  const dispatch = useAppDispatch()
  const authorisedUser = useAuthorisedUser()
  const platformDetail = useSelector(platformDetailSelector)
  const showAssignmentDialog = useFundShowAssignmentDialog()
  const showAllocationDialog = useShowAllocationDialog()
  const getLabel = (baseLabel: Translation) => {
    const count = isGroupRow ? ` (${items.length})` : ``
    return `${Locale.translate(baseLabel)}${count}`
  }

  const handleAssign = async () => {
    if (assignmentUnlocked(items[0].deal.fundAssignment, authorisedUser.username)) {
      showAssignmentDialog(items[0])
    } else {
      const dialogArgs = {
        type: NotificationType.Confirm,
        title: Translation.TC_ASSIGNMENT_LOCKED,
        message: Translation.SC_ASSIGNMENT_LOCKED_TO_ANOTHER_USER,
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        cancelButton: Translation.TC_BUTTON_NO,
        defaultButton: Translation.TC_BUTTON_YES
      }
      const dialogResult = await showDialog(dialogArgs)

      if (dialogResult === Translation.TC_BUTTON_YES) {
        showAssignmentDialog(items[0])
      }
    }
  }

  const handleAllocateCollateral = async () => {
    if (allocationUnlocked(items[0].deal.basketAllocation, authorisedUser.username)) {
      showAllocationDialog(items[0])
    } else {
      const dialogArgs = {
        type: NotificationType.Confirm,
        title: Translation.TC_ALLOCATION_LOCKED,
        message: Translation.SC_ALLOCATION_LOCKED_TO_ANOTHER_USER,
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        cancelButton: Translation.TC_BUTTON_NO,
        defaultButton: Translation.TC_BUTTON_YES
      }
      const dialogResult = await showDialog(dialogArgs)

      if (dialogResult === Translation.TC_BUTTON_YES) {
        showAllocationDialog(items[0])
      }
    }
  }

  const handleExtendAllocationTime = () => {
    dispatch(DealActions.extendAllocationTime(items[0].deal.dealId))
  }

  const handleOpenTicket = () => {
    items.forEach(dealDetail => {
      if (isOpenFin()) {
        dispatch(WindowActions.openTicketWindow(dealDetail.deal.dealId, EntityType.DEAL, getDealTitle(dealDetail)))
      } else {
        showModal(<Ticket dealId={dealDetail.deal.dealId} dealVersion={dealDetail.deal.version} />)
      }
    })
  }

  const handleTraderAmend = () => {
    const dealDetail = items[0]
    showModal(<ServicingTraderAmendDialog dealId={dealDetail.deal.dealId} />)
  }

  const showTradeBulkCancelDialog = async () => {
    const dealsId = items.map(dealDetail => dealDetail.deal.dealId)
    const isAdmin = items.every(item => canCancel(item))
    showModal(<ServicingTraderBulkCancelDialog dealIds={dealsId} isAdmin={isAdmin} />)
  }

  const handleServicingStartSubstitute = () => {
    const { deal } = items[0]
    if (deal.collateralId && deal.clearingHouseId === null) {
      showModal(<ServicingSubstitutionDialog dealId={deal.dealId} />)
    } else {
      throw new Error('DealServicingStartSubstituteEvent not allowed for non-bilateral or basket deals')
    }
  }

  const handleServicingAdminStartSubstitute = () => {
    const { deal } = items[0]
    if (deal.collateralId && deal.clearingHouseId === null) {
      showModal(<ServicingAdminSubstitutionDialog dealId={deal.dealId} />)
    } else {
      throw new Error('DealServicingStartSubstituteEvent not allowed for non-bilateral or basket deals')
    }
  }

  const handleServicingStartTerminate = () => {
    showModal(<ServicingTerminationDialog dealIds={items.map(item => item.deal.dealId)} />)
  }

  const handleServicingStartAdminTerminate = () => {
    showModal(<ServicingAdminTerminationDialog dealIds={items.map(item => item.deal.dealId)} />)
  }

  const handleServicingStartRerate = () => {
    showModal(<ServicingRerateDialog dealIds={items.map(item => item.deal.dealId)} />)
  }

  const handleServicingStartAdminRerate = () => {
    showModal(<ServicingAdminRerateDialog dealIds={items.map(item => item.deal.dealId)} />)
  }

  const handleServicingStartCloseOpen = () => {
    items.length === 1
      ? showModal(<ServicingCloseOpenDialog dealId={items[0].deal.dealId} />)
      : showModal(
          <ServicingBulkCloseOpenDialog canSend={canServicingSend} dealIds={items.map(item => item.deal.dealId)} adminCloseOpen={false} />
        )
  }

  const handleServicingStartAdminCloseOpen = () => {
    items.length === 1
      ? showModal(<ServicingAdminCloseOpenDialog dealId={items[0].deal.dealId} />)
      : showModal(
          <ServicingBulkCloseOpenDialog canSend={canServicingAccept} dealIds={items.map(item => item.deal.dealId)} adminCloseOpen={true} />
        )
  }

  const handleServicingStartReturnRecall = () => {
    showModal(<ServicingReturnRecallDialog dealId={items[0].deal.dealId} />)
  }

  const handleServicingStartRoll = async () => {
    const dialogArgs = {
      type: NotificationType.Confirm,
      title: Translation.TC_ROLL,
      message: Translation.SC_ROLL_CONFIRMATION,
      buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
      cancelButton: Translation.TC_BUTTON_NO,
      defaultButton: Translation.TC_BUTTON_YES
    }

    const mapDealDetailsToDealServicingStartRollEvent = (dealDetail: DealDetail): DealServicingStartRollEvent => {
      return {
        dealId: dealDetail.deal.dealId,
        eventType: 'DealServicingStartRollEvent',
        correlationId: uuidv4(),
        externalServicingId: null
      }
    }

    if ((await showDialog(dialogArgs)) === Translation.TC_BUTTON_YES) {
      const childEvents = items.map(mapDealDetailsToDealServicingStartRollEvent)
      dispatch(DealActions.servicingStartRollBulk({ childEvents }))
    }
  }

  const handleServicingStartRollInPlace = () => {
    showModal(<ServicingRollInPlaceDialog dealIds={items.map(item => item.deal.dealId)} />)
  }

  const handleViewRelatedTradesClick = () => {
    dispatch(RelatedDealsThunks.updateRelatedDealsSubscription(items[0].deal.originalDealId))
  }

  const commonActions = useCommonDealActionItems(items)

  const actions: ActionItem[] = [
    {
      label: getLabel(Translation.TC_ACTION_ASSIGN),
      type: SplitButtonType.Bad,
      onClick: handleAssign,
      enabled: items.length === 1 && canSaveAssignments(items[0])
    },
    {
      label: getLabel(Translation.TC_ACTION_ALLOCATE_COLLATERAL),
      type: SplitButtonType.Bad,
      onClick: handleAllocateCollateral,
      enabled: items.length === 1 && canSaveAllocations(items[0])
    },
    {
      label: getLabel(Translation.TC_ACTION_EXTEND_ALLOCATION_TIME),
      type: SplitButtonType.Bad,
      onClick: handleExtendAllocationTime,
      enabled: items.length === 1 && canExtendAllocationTime(items[0])
    },
    {
      label: getLabel(Translation.TC_ACTION_OPEN_TICKET),
      onClick: handleOpenTicket,
      enabled: true
    },
    {
      label: getLabel(Translation.TC_ACTION_AMEND_TRADE),
      type: SplitButtonType.Bad,
      onClick: handleTraderAmend,
      enabled: items.length === 1 && canServicingTraderStartAmend(items[0])
    },
    {
      label: getLabel(Translation.TC_ACTION_CANCEL_TRADE),
      type: SplitButtonType.Bad,
      onClick: showTradeBulkCancelDialog,
      enabled: items.length === 1 && canServicingTraderCancel(items[0])
    },
    {
      label: getLabel(Translation.TC_ACTION_CANCEL_TRADE),
      type: SplitButtonType.Bad,
      onClick: showTradeBulkCancelDialog,
      enabled: items.length !== 1 && items.every(item => canServicingTraderCancel(item))
    },
    {
      label: getLabel(Translation.TC_ACTION_CANCEL_TRADE),
      onClick: showTradeBulkCancelDialog,
      enabled: items.every(item => canAdminCancel(item))
    },
    {
      label: getLabel(Translation.TC_ACTION_CANCEL_TRADE),
      onClick: createConfirmationHandler(
        Translation.TC_CANCEL_DEAL,
        Translation.SC_CANCEL_DEAL_CONFIRMATION,
        dealId => dispatch(DealActions.clearingFailureManuallyCancel(dealId)),
        showDialog,
        items
      ),
      enabled: items.every(item => canClearingFailureManuallyCancel(item))
    },
    {
      label: getLabel(Translation.TC_ACTION_RETRY_TRADE),
      onClick: createConfirmationHandler(
        Translation.TC_RETRY_DEAL,
        Translation.SC_RETRY_DEAL_CONFIRMATION,
        dealId => dispatch(DealActions.clearingFailureRetry(dealId)),
        showDialog,
        items
      ),
      enabled: items.every(item => canClearingFailureRetry(item))
    },
    {
      label: getLabel(Translation.TC_ACTION_COMPLETE_TRADE),
      onClick: createConfirmationHandler(
        Translation.TC_COMPLETE_DEAL,
        Translation.SC_COMPLETE_DEAL_CONFIRMATION,
        dealId => dispatch(DealActions.clearingFailureManuallyComplete(dealId)),
        showDialog,
        items
      ),
      enabled: items.every(item => canClearingFailureManuallyComplete(item))
    },
    {
      label: getLabel(Translation.TC_ACTION_SUBSTITUTE),
      onClick: handleServicingStartSubstitute,
      enabled: items.length === 1 && canServicingStartSubstitute(items[0]) && collateralMarketNotSuspendedOrClosed(items, platformDetail)
    },
    {
      id: Translation.TC_ACTION_SUBSTITUTE,
      label: getLabel(Translation.TC_ACTION_SUBSTITUTE),
      onClick: handleServicingAdminStartSubstitute,
      enabled:
        items.length === 1 && canServicingAdminStartSubstitute(items[0]) && collateralMarketNotSuspendedOrClosed(items, platformDetail)
    },
    {
      label: getLabel(Translation.TC_ACTION_TERMINATE),
      onClick: handleServicingStartTerminate,
      enabled: canServicingStartTerminate(items) && collateralMarketNotSuspendedOrClosed(items, platformDetail)
    },
    {
      label: getLabel(Translation.TC_ACTION_TERMINATE),
      onClick: handleServicingStartAdminTerminate,
      enabled: canServicingStartAdminTerminate(items) && collateralMarketNotSuspendedOrClosed(items, platformDetail)
    },
    {
      label: getLabel(Translation.TC_ACTION_RERATE),
      onClick: handleServicingStartRerate,
      enabled: canServicingStartRerate(items) && collateralMarketNotSuspendedOrClosed(items, platformDetail)
    },
    {
      id: Translation.TC_ACTION_RERATE,
      label: getLabel(Translation.TC_ACTION_RERATE),
      onClick: handleServicingStartAdminRerate,
      enabled: canServicingStartAdminRerate(items) && collateralMarketNotSuspendedOrClosed(items, platformDetail)
    },
    {
      id: Translation.TC_ACTION_CLOSE_OPEN,
      label: getLabel(Translation.TC_ACTION_CLOSE_OPEN),
      onClick: handleServicingStartCloseOpen,
      enabled: canServicingStartCloseOpen(items) && collateralMarketNotSuspendedOrClosed(items, platformDetail)
    },
    {
      id: Translation.TC_ACTION_CLOSE_OPEN,
      label: getLabel(Translation.TC_ACTION_CLOSE_OPEN),
      onClick: handleServicingStartAdminCloseOpen,
      enabled: canServicingStartAdminCloseOpen(items) && collateralMarketNotSuspendedOrClosed(items, platformDetail)
    },
    {
      id: Translation.TC_ACTION_RETURN_RECALL,
      label: getLabel(Translation.TC_ACTION_RETURN_RECALL),
      onClick: handleServicingStartReturnRecall,
      enabled: items.length === 1 && canServicingStartReturnRecall(items[0]) && collateralMarketNotSuspendedOrClosed(items, platformDetail)
    },
    {
      id: Translation.TC_ACTION_ROLL,
      label: getLabel(Translation.TC_ACTION_ROLL),
      onClick: handleServicingStartRoll,
      enabled: canServicingStartRoll(items) && collateralMarketNotSuspendedOrClosed(items, platformDetail)
    },
    {
      id: Translation.TC_ACTION_ROLL_IN_PLACE,
      label: getLabel(Translation.TC_ACTION_ROLL_IN_PLACE),
      onClick: handleServicingStartRollInPlace,
      enabled: canServicingStartRollInPlace(items) && collateralMarketNotSuspendedOrClosed(items, platformDetail)
    },
    {
      label: getLabel(Translation.TC_VIEW_RELATED_TRADES),
      onClick: handleViewRelatedTradesClick,
      enabled: canViewRelatedTrades(items)
    },
    ...commonActions
  ]

  return <SplitButtonDropdown dataTestId={'DealActionsButton'} actions={actions} disabled={disabled} maxButtons={1} />
}
